import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { listFiles } from "../api/files";
import { deleteFile } from "../api/files";
import { useToast } from "../context/ToastContext";
import { useNavigate } from "react-router-dom";

const STATUS_BADGE = {
  indexed: "bg-green-900/40 text-green-400 border-green-800",
  pending: "bg-yellow-900/40 text-yellow-400 border-yellow-800",
  failed: "bg-red-900/40 text-red-400 border-red-800",
  pending_approval: "bg-orange-900/40 text-orange-400 border-orange-800",
};

export default function MyFilesPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: files, isLoading } = useQuery({
    queryKey: ["my-files"],
    queryFn: async () => (await listFiles()).data.data,
    refetchInterval: (q) => {
      const list = q.state.data || [];
      return list.some((f) => ["pending", "pending_approval"].includes(f.indexed_status))
        ? 3000
        : false;
    },
  });

  const delMut = useMutation({
    mutationFn: deleteFile,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-files"] });
      qc.invalidateQueries({ queryKey: ["files"] });
      toast("File deleted", "success");
    },
    onError: (err) => toast(err.response?.data?.message || "Delete failed", "error"),
  });

  const handleDelete = (f) => {
    if (!window.confirm(`Delete "${f.original_name}"?`)) return;
    delMut.mutate(f.id);
  };

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col">
      <div className="mx-auto w-[75%] max-w-2xl pt-10 pb-3 flex items-center gap-3 flex-shrink-0">
        <button
          onClick={() => navigate("/")}
          className="text-dark-muted hover:text-dark-text transition-colors"
        >
          <ion-icon name="arrow-back-outline" style={{ fontSize: "20px" }} />
        </button>
        <h1 className="text-base font-semibold text-dark-text">My Files</h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-[75%] max-w-2xl py-8">
          {isLoading ? (
            <p className="text-sm text-dark-muted">Loading…</p>
          ) : (
            <div className="rounded-lg border border-dark-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-dark-surface text-xs text-dark-muted">
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Type</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(files || []).map((f) => (
                    <tr key={f.id} className="border-t border-dark-border">
                      <td className="px-3 py-2 text-dark-text truncate max-w-[220px]">
                        {f.original_name}
                      </td>
                      <td className="px-3 py-2 text-dark-muted uppercase text-xs">
                        {f.file_type}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`rounded-full border px-2 py-0.5 text-xs ${
                            STATUS_BADGE[f.indexed_status] || ""
                          }`}
                        >
                          {f.indexed_status}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button
                          onClick={() => handleDelete(f)}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!files?.length && (
                    <tr>
                      <td colSpan={4} className="px-3 py-6 text-center text-dark-muted text-xs">
                        No files yet — upload from the main chat page
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
